import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import EditCompetitionModal from "../components/grants/competitions/EditCompetitionModal";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import { formatDate, formatDateTime } from "../utils/date-formatters";
import { competitionStatusLabels, competitionStatusColors } from "../constants/grants-labels";

interface CompetitionDetails {
  id: string;
  name: string;
  code: string | null;
  actionCode: string | null;
  description: string | null;
  targetGroup: string | null;
  status: string;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  officialUrl: string | null;
  isActive: boolean;
  program: {
    id: string;
    code: string;
    name: string;
    shortName: string | null;
  } | null;
  callsCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Page component for displaying detailed information about a single competition.
 */
const GrantsCompetitionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, hasModuleEnabled } = useHotel();
  const canWrite = user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: competition, isLoading, error, refetch } = useQuery<CompetitionDetails>({
    queryKey: ["grants-competition", id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/grants/competitions/${id}`);
      return data;
    },
    enabled: !!id,
  });

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="animate-pulse">
            <div className="h-4 bg-neutral-200 rounded w-48 mb-4"></div>
            <div className="h-8 bg-neutral-200 rounded w-96 mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded w-64"></div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-neutral-200 rounded w-32"></div>
              <div className="h-4 bg-neutral-200 rounded w-full"></div>
              <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
              <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !competition) {
    return (
      <Layout>
        <div className="space-y-6">
          <nav className="text-sm text-neutral-500">
            <Link to="/grants/competitions" className="hover:text-primary-600">
              Konkursy
            </Link>
            <span className="mx-2">&gt;</span>
            <span>Błąd</span>
          </nav>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-red-600 mb-4">
              <svg
                className="h-12 w-12 mx-auto mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="font-medium">
                Nie udało się pobrać danych konkursu
              </p>
              <p className="text-sm mt-1">
                {error instanceof Error
                  ? error.message
                  : "Konkurs nie został znaleziony"}
              </p>
            </div>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => refetch()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Spróbuj ponownie
              </button>
              <Link
                to="/grants/competitions"
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
              >
                Wróć do listy
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumbs */}
        <nav className="text-sm text-neutral-500" aria-label="Nawigacja breadcrumb">
          <Link to="/grants/competitions" className="hover:text-primary-600">
            Konkursy
          </Link>
          <span className="mx-2">&gt;</span>
          <span className="text-neutral-900">{competition.name}</span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              {competition.program && (
                <Link
                  to={`/grants/programs/${competition.program.id}`}
                  className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 hover:bg-primary-200"
                >
                  {competition.program.code}
                </Link>
              )}
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  competitionStatusColors[competition.status] ||
                  "bg-neutral-100 text-neutral-800"
                }`}
              >
                {competitionStatusLabels[competition.status] || competition.status}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left mt-2">{competition.name}</h1>
            {competition.code && (
              <p className="text-sm text-neutral-500 text-left">{competition.code}</p>
            )}
          </div>
          {canWrite && (
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Edytuj
            </button>
          )}
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-lg font-medium text-neutral-900 text-left">
              Szczegóły konkursu
            </h2>
          </div>
          <div className="px-6 py-4">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Program</dt>
                <dd className="mt-1 text-sm text-left">
                  {competition.program ? (
                    <Link
                      to={`/grants/programs/${competition.program.id}`}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      {competition.program.name}
                    </Link>
                  ) : (
                    <span className="text-neutral-500">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Kod działania</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {competition.actionCode || "-"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Grupa docelowa</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {competition.targetGroup || "-"}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Liczba naborów</dt>
                <dd className="mt-1 text-sm text-left">
                  {competition.callsCount > 0 ? (
                    <Link
                      to={`/grants/calls?competitionId=${competition.id}`}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      {competition.callsCount}
                    </Link>
                  ) : (
                    <span className="text-neutral-500">0</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Data rozpoczęcia</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDate(competition.applicationStartDate)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Data zakończenia</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDate(competition.applicationEndDate)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Oficjalna strona</dt>
                <dd className="mt-1 text-sm text-left">
                  {competition.officialUrl ? (
                    <a
                      href={competition.officialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800"
                    >
                      Otwórz stronę
                    </a>
                  ) : (
                    <span className="text-neutral-500">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Status aktywności</dt>
                <dd className="mt-1 text-sm text-left">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      competition.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-neutral-200 text-neutral-800"
                    }`}
                  >
                    {competition.isActive ? "Aktywny" : "Nieaktywny"}
                  </span>
                </dd>
              </div>
              {competition.description && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-neutral-500 text-left">Opis</dt>
                  <dd className="mt-1 text-sm text-neutral-900 text-left whitespace-pre-wrap">
                    {competition.description}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Utworzono</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDateTime(competition.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-neutral-500 text-left">Zaktualizowano</dt>
                <dd className="mt-1 text-sm text-neutral-900 text-left">
                  {formatDateTime(competition.updatedAt)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <EditCompetitionModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        competitionId={id}
      />
    </Layout>
  );
};

export default GrantsCompetitionDetails;
